import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import Grid from '../../../components/components/Grid/Grid';
import { Spinner } from '../../../components';
import LookupService from '../../../Services/LookupService';
import { CommonUtility } from '../../../Services/CommonUtility';
import AppContext from '../../../shared/components/AppContext';
import { useConfirmationModalContext } from '../../../shared/components/ModalConfirmationContext';
import { SelectOption } from '../../../shared/components/input';
import { RootState } from '../../../shared/store';
import { updateLookupData } from '../../../customer/customer-slice/Customer-lookup-slice';
import * as constant from '../../../shared/util/constant';
import { EMPTY_GUID } from '../Model/CompanyCommissionRate';
import { CompanyRetentionRateDto, CreateCompanyRetentionRateDto, UpdateCompanyRetentionRateDto } from '../Model/CompanyRetentionRate';
import { LobPolicyTypeMapping } from '../Model/LobPolicyTypeMapping';
import CarrierManagementAPI from '../Services/CarrierManagementAPI';
import PolicyManagementAPI from '../Services/PolicyManagementAPI';
import RetentionArrayService, { toIdList } from './RetentionArrayService';
import RetentionArraySection, { RetentionArraySectionConfig } from './RetentionArraySection';
import '../CarrierCommission/CarrierCommissionGrid.scss';

interface Props {
    companyId?: string;
    parentAgency?: string;
    isActive?: boolean;
    canCreate?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
}

const toNullableNumber = (value: any): number | null => value === '' || value === null || value === undefined ? null : Number(value);

const normalizeLookupToken = (value: any): string => String(value || '').trim().replace(/^\{(.+)\}$/, '$1').toLowerCase();

const resolveLookupValue = (options: SelectOption[], value: any): any => {
    if (value === null || value === undefined || value === '') return '';
    const normalizedValue = normalizeLookupToken(value);
    const option = options.find(item =>
        normalizeLookupToken(item.value) === normalizedValue ||
        normalizeLookupToken(item.label) === normalizedValue ||
        normalizeLookupToken(item.originalData?.lookupCode) === normalizedValue
    );
    return option?.value || value;
};

const toLineOfBusinessOption = (item: any): SelectOption => {
    const source = item?.originalData || item || {};
    return {
        label: source.lookupValue || source.LookupValue || source.label || source.Label || '',
        value: source.id || source.Id || source.value || source.Value || '',
        originalData: source
    };
};

// Only these lines of business are shown in the dropdown, regardless of what the API returns.
const ALLOWED_LINES_OF_BUSINESS = ['Homeowners', 'Private Passenger Auto', 'Flood', 'Earthquake (C)','Earthquake (P)', 'Dwelling Fire', 'Umbrella (P)','Umbrella(C)'];

const ADDITIONAL_RETENTION_ARRAY_SECTIONS: RetentionArraySectionConfig[] = [
    {
        typeId: constant.CarrierManagementConstants.RETENTION_ARRAY_TYPES.NON_RENEWAL,
        title: 'Non Renewals',
        messageLabel: 'Non Renewal',
        includeRateFields: false
    },
    {
        typeId: constant.CarrierManagementConstants.RETENTION_ARRAY_TYPES.CANCELLATION,
        title: 'Cancellation Request (XLN) and Received (XLC)',
        messageLabel: 'Cancellation Request',
        includeRateFields: false,
        includeCancellationReason: true
    }
];

const RetentionArrayGrid: React.FC<Props> = ({ companyId, parentAgency, isActive = true, canCreate = true, canUpdate = true, canDelete = true }) => {
    const appContext = useContext(AppContext);
    const modalContext = useConfirmationModalContext();
    const dispatch = useDispatch();
    const apiRef = useRef(new CarrierManagementAPI());
    const policyApiRef = useRef(new PolicyManagementAPI());
    const serviceRef = useRef(new RetentionArrayService());
    const reduxStateUSList = useSelector((state: RootState) => state?.LookupState.stateUSList);
    const reduxLOBList = useSelector((state: RootState) => state?.LookupState.lineOfBusinessList);
    const reduxPolicyTypeList = useSelector((state: RootState) => state?.LookupState.policyTypeList);
    const reduxCancellationReasonList = useSelector((state: RootState) => state?.LookupState.cancellationReasonList);
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [navigateToFirstPage, setNavigateToFirstPage] = useState(false);
    const [additionalSectionDirtyState, setAdditionalSectionDirtyState] = useState<Record<string, boolean>>({});
    const carriersRef = useRef<any[]>([]);
    const statesRef = useRef<SelectOption[]>([]);
    const lineOfBusinessesRef = useRef<SelectOption[]>([]);
    const policyTypesRef = useRef<SelectOption[]>([]);
    const cancellationReasonsRef = useRef<SelectOption[]>([]);
    const lobPolicyTypeMappingsRef = useRef<LobPolicyTypeMapping[]>([]);

    const hasUnsavedChanges = isDirty || Object.keys(additionalSectionDirtyState)
        .some(typeId => additionalSectionDirtyState[typeId]);

    useEffect(() => { appContext.formSavedStatus = !hasUnsavedChanges; }, [hasUnsavedChanges]);

    useEffect(() => {
        const beforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        if (!isActive && isDirty) {
            setRows(current => current.filter(row => row.id !== EMPTY_GUID)
                .map(row => ({ ...row, enableGridRowEditing: false, isGridInEditMode: false, editingRow: undefined })));
            setIsDirty(false);
        }
        if (!isActive) setAdditionalSectionDirtyState({});
    }, [isActive]);

    useEffect(() => {
        const loadLookups = async () => {
            try {
                const [stateData, lineOfBusinessData, policyTypeData, carrierData, lobPolicyTypeMappings, cancellationReasonData] = await Promise.all([
                    LookupService.fetchUSStates(),
                    reduxLOBList.length > 0 && reduxLOBList.some(option => option.originalData)
                        ? Promise.resolve(null)
                        : LookupService.fetchLineofBusiness(),
                    reduxPolicyTypeList.length > 0
                        ? Promise.resolve(null)
                        : LookupService.fetchPolicyType(),
                    apiRef.current.getATMSRatingCompanies(),
                    policyApiRef.current.getLobPolicyTypeMappings(),
                    reduxCancellationReasonList?.length > 0
                        ? Promise.resolve(null)
                        : LookupService.fetchCancellationReason()
                ]);
                if (stateData) {
                    const stateOptions = stateData.map((state: any) => ({ label: state.lookupCode, value: state.lookupCode }));
                    dispatch(updateLookupData({ data: stateOptions, type: constant.SlicerLookUpConstant.US_STATE }));
                }
                if (lineOfBusinessData) {
                    const lineOfBusinessOptions = lineOfBusinessData.map(toLineOfBusinessOption);
                    dispatch(updateLookupData({ data: lineOfBusinessOptions, type: constant.SlicerLookUpConstant.Line_Of_Business }));
                }
                if (policyTypeData) {
                    const policyTypeOptions = policyTypeData.map((item: any) => ({ label: item.lookupValue, value: item.id }));
                    dispatch(updateLookupData({ data: policyTypeOptions, type: constant.SlicerLookUpConstant.POLICY_TYPE }));
                }
                const cancellationReasonOptions: SelectOption[] = cancellationReasonData
                    ? CommonUtility.mapToSelectOptions(cancellationReasonData)
                    : [...reduxCancellationReasonList];
                if (cancellationReasonData) {
                    dispatch(updateLookupData({ data: cancellationReasonOptions, type: constant.SlicerLookUpConstant.Cancellation_Reason }));
                }
                cancellationReasonsRef.current = cancellationReasonOptions;
                const stateOptions: SelectOption[] = (stateData
                    ? stateData.map((state: any) => ({ label: state.lookupCode, value: state.id }))
                    : [...reduxStateUSList])
                    .sort((left, right) => String(left.label).localeCompare(String(right.label)));
                const lineOfBusinessOptions: SelectOption[] = (lineOfBusinessData || reduxLOBList)
                    .map(toLineOfBusinessOption)
                    .filter(option => Boolean(option.label && option.value) && ALLOWED_LINES_OF_BUSINESS.includes(option.label));
                const policyTypeOptions: SelectOption[] = policyTypeData
                    ? policyTypeData.map((item: any) => ({ label: item.lookupValue, value: item.id }))
                    : [...reduxPolicyTypeList];
                statesRef.current = stateOptions;
                lineOfBusinessesRef.current = lineOfBusinessOptions;
                policyTypesRef.current = policyTypeOptions;
                lobPolicyTypeMappingsRef.current = lobPolicyTypeMappings || [];
                const carrierOptions: SelectOption[] = (carrierData || [])
                    .map(carrier => ({
                        label: carrier.companyShortName || carrier.companyName,
                        value: carrier.companyId
                    }))
                    .filter(carrier => Boolean(carrier.label && carrier.value))
                    .sort((left, right) => left.label.localeCompare(right.label));
                carriersRef.current = carrierOptions;
                setColumns(serviceRef.current.getGridColumns(onEditingCellDataChange, gridCellClickHandler,
                    stateOptions, lineOfBusinessOptions, policyTypeOptions, carrierOptions, !canUpdate, !canDelete,
                    true, lobPolicyTypeMappingsRef.current));
                setLookupsLoaded(true);
            } catch {
                toast.error('Error loading Retention Array lookup data.');
            }
        };
        loadLookups();
    }, []);

    useEffect(() => {
        setHasFetched(false);
    }, [companyId, parentAgency]);

    useEffect(() => {
        if (companyId && parentAgency && isActive && lookupsLoaded && !hasFetched) fetchRows();
    }, [companyId, parentAgency, isActive, lookupsLoaded, hasFetched]);

    const fetchRows = async () => {
        if (!parentAgency) return;
        setLoading(true);
        try {
            const data = await apiRef.current.getRetentionRates(
                constant.CarrierManagementConstants.RETENTION_ARRAY_TYPES.PREMIUM_DIFFERENCE,
                parentAgency
            );
            const enrichedRows = data.map((row: any) => {
                const lineOfBusinessValue = row.lineOfBusiness ?? row.lineOFBusiness ?? '';
                const enriched = {
                    ...row,
                    lineOfBusiness: resolveLookupValue(lineOfBusinessesRef.current, lineOfBusinessValue),
                    lineOfBusinessSourceDisplay: typeof lineOfBusinessValue === 'object'
                        ? lineOfBusinessValue.lookupValue || lineOfBusinessValue.LookupValue || lineOfBusinessValue.label || lineOfBusinessValue.Label || ''
                        : lineOfBusinessValue,
                    APlusCarrierToRate: toIdList(row.APlusCarrierToRate ?? row.aPlusCarrierToRate, true),
                    enableGridRowEditing: false,
                    isGridInEditMode: false
                };
                serviceRef.current.enrichDisplayFields(enriched, carriersRef.current, lineOfBusinessesRef.current, policyTypesRef.current);
                return enriched;
            });
            setRows(enrichedRows);
            setIsDirty(false);
            setHasFetched(true);
        } catch {
            toast.error('Error loading Retention Array.');
        } finally {
            setLoading(false);
        }
    };

    const onEditingCellDataChange = (editingRow: any, column: any, value: any, _additionalInfo: any) => {
        editingRow[column.fieldName] = value;
        if (column.fieldName === 'lineOfBusiness') editingRow.policyType = '';
        if (column.fieldName === 'percentage' && value !== '') editingRow.dollar = null;
        if (column.fieldName === 'dollar' && value !== '') editingRow.percentage = null;
        setRows(current => [...current]);
    };

    const gridCellClickHandler = async (row: any, _column: any, action: string) => {
        if (action === 'save_row') await saveRow(row);
        if (action === 'cancel_row_editing') await cancelRow(row);
        if (action === 'edit_row' && canUpdate) editRow(row);
        if (action === 'delete_row' && canDelete) await deleteRow(row);
    };

    const editRow = (row: any) => {
        row.editingRow = {
            ...row,
            lineOfBusiness: resolveLookupValue(
                lineOfBusinessesRef.current,
                row.lineOfBusiness || row.lineOfBusinessSourceDisplay || row.lineOfBusinessDisplay
            )
        };
        row.enableGridRowEditing = true;
        setRows(current => current.map(item => ({ ...item, isGridInEditMode: true })));
        setIsDirty(true);
    };

    const saveRow = async (row: any) => {
        const errors = serviceRef.current.validateRow(row);
        row.editingRow.errors = errors;
        if (Object.keys(errors).length) {
            toast.warn('Please fix validation errors before saving.');
            setRows(current => [...current]);
            return;
        }
        const editingRow = row.editingRow;
        const dto: CompanyRetentionRateDto = {
            id: editingRow.id || EMPTY_GUID,
            companyId: companyId || editingRow.companyId || '',
            parentAgency: parentAgency || editingRow.parentAgency || '',
            state: editingRow.state,
            lineOfBusiness: editingRow.lineOfBusiness,
            policyType: editingRow.policyType,
            percentage: toNullableNumber(editingRow.percentage),
            dollar: toNullableNumber(editingRow.dollar),
            APlusCarrierToRate: toIdList(editingRow.APlusCarrierToRate, true)
        };
        try {
            if (dto.id === EMPTY_GUID) {
                const createRequest: CreateCompanyRetentionRateDto = {
                    parentAgency: dto.parentAgency,
                    retentionArrayType: constant.CarrierManagementConstants.RETENTION_ARRAY_TYPES.PREMIUM_DIFFERENCE,
                    state: dto.state,
                    lineOfBusiness: dto.lineOfBusiness,
                    policyType: dto.policyType,
                    percentage: dto.percentage,
                    dollar: dto.dollar,
                    APlusCarrierToRate: dto.APlusCarrierToRate
                };
                await apiRef.current.createRetentionRate(createRequest);
                toast.success('Premium Difference created successfully.');
            } else {
                const updateRequest: UpdateCompanyRetentionRateDto = {
                    id: dto.id,
                    parentAgency: dto.parentAgency,
                    retentionArrayType: constant.CarrierManagementConstants.RETENTION_ARRAY_TYPES.PREMIUM_DIFFERENCE,
                    state: dto.state,
                    lineOfBusiness: dto.lineOfBusiness,
                    policyType: dto.policyType,
                    percentage: dto.percentage,
                    dollar: dto.dollar,
                    APlusCarrierToRate: dto.APlusCarrierToRate
                };
                await apiRef.current.updateRetentionRate(updateRequest);
                toast.success('Premium Difference updated successfully.');
            }
            await fetchRows();
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || 'Error saving Premium Difference.');
        }
    };

    const cancelRow = async (row: any) => {
        if (!await modalContext.showConfirmation('Are you sure you want to discard changes?')) return;
        setRows(current => current
            .filter(item => item !== row || row.id !== EMPTY_GUID)
            .map(item => ({ ...item, enableGridRowEditing: false, isGridInEditMode: false, editingRow: undefined })));
        setIsDirty(false);
    };

    const deleteRow = async (row: any) => {
        if (!await modalContext.showConfirmation('Are you sure you want to delete this Premium Difference?')) return;
        try {
            if (row.id !== EMPTY_GUID) await apiRef.current.deleteRetentionRate(row.id);
            toast.success('Premium Difference deleted successfully.');
            await fetchRows();
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || 'Error deleting Premium Difference.');
        }
    };

    const addRow = () => {
        if (!companyId || !parentAgency) return;
        setRows(current => [
            serviceRef.current.getNewRow(companyId, parentAgency),
            ...current.map(row => ({ ...row, isGridInEditMode: true }))
        ]);
        setIsDirty(true);
        setNavigateToFirstPage(true);
    };

    const handleAdditionalSectionDirtyChange = (typeId: string, sectionIsDirty: boolean) => {
        setAdditionalSectionDirtyState(current => current[typeId] === sectionIsDirty
            ? current
            : { ...current, [typeId]: sectionIsDirty });
    };

    return <div>
        <section className="carrier-commission-grid-container p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold mb-0">Premium Difference</h5>
                <Button label="Add Row" icon="bi bi-plus-circle" onClick={addRow}
                    disabled={isDirty || !canCreate} className="p-button-sm" />
            </div>
            {loading && <Spinner loading={true} type="SyncLoader" />}
            {!loading && columns.length > 0 && <div className="commission-grid-div">
                <Grid data={rows} showFilters={false} id="retentionArrayGrid" retainGridState={true}
                    columns={columns} hiddenColumns={['selection']} sortDefault={[]} paginationDisabled={true}
                    grdTabelClass="table-striped table-sm retention-array-table" grdTableContainerClass="commission-grid-table-container"
                    zindexHeader={10000} navigateToFirstPage={navigateToFirstPage}
                    onNavigateToFirstPage={() => setNavigateToFirstPage(false)} />
            </div>}
        </section>
        {lookupsLoaded && ADDITIONAL_RETENTION_ARRAY_SECTIONS.map(section => <RetentionArraySection
            key={section.typeId}
            companyId={companyId}
            parentAgency={parentAgency}
            isActive={isActive}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            config={section}
            stateOptions={statesRef.current}
            lineOfBusinessOptions={lineOfBusinessesRef.current}
            policyTypeOptions={policyTypesRef.current}
            lobPolicyTypeMappings={lobPolicyTypeMappingsRef.current}
            carrierOptions={carriersRef.current}
            cancellationReasonOptions={cancellationReasonsRef.current}
            onDirtyChange={handleAdditionalSectionDirtyChange}
        />)}
    </div>;
};

export default RetentionArrayGrid;